/**
 * CommercialProductsList — searchable/filterable list of commercial dosing products.
 *
 * Same UX patterns as TestSelector: brand chips + parameter dropdown + text search.
 * Persists filter state to sessionStorage.
 */
import { useState, useMemo } from 'react';
import type { CommercialProduct } from '@reefapp/shared';
import { IconSearch } from './icons';

const STORAGE_KEY_BRAND = 'reefapp:commercial-products:brand-filter';
const STORAGE_KEY_PARAM = 'reefapp:commercial-products:parameter-filter';

function readSessionStorage(key: string, fallback: string): string {
  try {
    return sessionStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}

function writeSessionStorage(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* noop */
  }
}

/** Extract a flat array of parameter strings from a product. */
function paramsOf(p: CommercialProduct): string[] {
  const param = p.parameter;
  if (Array.isArray(param)) return param as string[];
  return [param as string];
}

export interface CommercialProductsListProps {
  products: ReadonlyArray<CommercialProduct>;
  selectedProductId: string | null;
  onSelectProduct(product: CommercialProduct): void;
}

export function CommercialProductsList({
  products,
  selectedProductId,
  onSelectProduct,
}: CommercialProductsListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrand, setSelectedBrand] = useState(
    () => readSessionStorage(STORAGE_KEY_BRAND, 'All'),
  );
  const [selectedParameter, setSelectedParameter] = useState(
    () => readSessionStorage(STORAGE_KEY_PARAM, 'All'),
  );

  const brands = useMemo(() => {
    const set = new Set<string>();
    for (const p of products) {
      set.add(p.brand);
    }
    return ['All', ...[...set].sort()];
  }, [products]);

  const parameters = useMemo(() => {
    const set = new Set<string>();
    for (const p of products) {
      for (const param of paramsOf(p)) {
        set.add(param);
      }
    }
    return ['All', ...[...set].sort()];
  }, [products]);

  const filtered = useMemo(() => {
    const lowerCaseSearch = searchTerm.toLowerCase();
    return products.filter((p) => {
      if (selectedBrand !== 'All' && p.brand !== selectedBrand) return false;
      if (selectedParameter !== 'All') {
        const productParams = paramsOf(p);
        if (!productParams.includes(selectedParameter)) return false;
      }
      if (lowerCaseSearch) {
        return (
          p.name.toLowerCase().includes(lowerCaseSearch) ||
          p.brand.toLowerCase().includes(lowerCaseSearch) ||
          paramsOf(p).some((param) =>
            param.toLowerCase().includes(lowerCaseSearch),
          )
        );
      }
      return true;
    });
  }, [searchTerm, selectedBrand, selectedParameter, products]);

  return (
    <div className="bg-slate-800 p-4 rounded-2xl border border-slate-700 shadow-lg">
      <h2 className="text-xl font-semibold mb-4 text-cyan-300">Products</h2>

      {/* Text search */}
      <div className="relative mb-3">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <IconSearch className="h-4 w-4 text-slate-500" />
        </div>
        <input
          type="search"
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="block w-full rounded-md border-slate-700 bg-slate-900 py-2 pl-9 pr-3 text-sm text-slate-200 shadow-sm placeholder:text-slate-500 focus:border-cyan-500 focus:ring-cyan-500"
        />
      </div>

      {/* Brand chips */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {brands.map((brand) => (
          <button
            key={brand}
            onClick={() => {
              setSelectedBrand(brand);
              writeSessionStorage(STORAGE_KEY_BRAND, brand);
            }}
            className={`px-2.5 py-1 text-xs font-semibold border rounded-full transition-all duration-200 ${
              selectedBrand === brand
                ? 'bg-cyan-600 text-white border-cyan-600'
                : 'bg-slate-900 text-slate-400 border-slate-600 hover:bg-slate-700 hover:text-slate-300'
            }`}
          >
            {brand}
          </button>
        ))}
      </div>

      {/* Parameter filter */}
      <div className="mb-3">
        <select
          value={selectedParameter}
          onChange={(e) => {
            setSelectedParameter(e.target.value);
            writeSessionStorage(STORAGE_KEY_PARAM, e.target.value);
          }}
          className="block w-full rounded-md border-slate-700 bg-slate-900 py-1.5 px-2 text-xs text-slate-200 shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
          aria-label="Filter by parameter"
        >
          {parameters.map((p) => (
            <option key={p} value={p}>
              {p === 'All' ? 'All parameters' : p}
            </option>
          ))}
        </select>
      </div>

      {/* Product list */}
      {filtered.length > 0 ? (
        <div className="space-y-1 max-h-96 overflow-y-auto">
          {filtered.map((product) => {
            const isSelected = product.id === selectedProductId;
            return (
              <button
                key={product.id}
                onClick={() => onSelectProduct(product)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors duration-150 ${
                  isSelected
                    ? 'bg-cyan-600/20 border border-cyan-600 text-cyan-200'
                    : 'bg-slate-900 border border-transparent text-slate-300 hover:bg-slate-700'
                }`}
              >
                <span className="font-semibold">{product.name}</span>
                <span className="block text-xs text-slate-500 mt-0.5">
                  {product.brand}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8 text-slate-500">
          <p className="text-sm">No products match your filters.</p>
          <button
            onClick={() => {
              setSearchTerm('');
              setSelectedBrand('All');
              writeSessionStorage(STORAGE_KEY_BRAND, 'All');
              setSelectedParameter('All');
              writeSessionStorage(STORAGE_KEY_PARAM, 'All');
            }}
            className="mt-2 px-3 py-1 text-xs font-semibold text-slate-300 bg-slate-700 rounded-md hover:bg-slate-600 transition-colors"
          >
            Clear filters
          </button>
        </div>
      )}
    </div>
  );
}